import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useDrawingsApi from '../hooks/useDrawingsApi';
import { Document, Page, pdfjs } from 'react-pdf';
// PDF.js worker setup using bundled entry
// Use PDF.js worker from CDN for consistent loading
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
import PDFViewer from '../../Takeoffs/components/PDFViewer';
import DrawingTreeView from '../components/DrawingTreeView';
import ReviewDrawingsTable from '../components/ReviewDrawingsTable';
import EditDrawingModal from '../components/EditDrawingModal';
import { toast } from 'react-toastify';

/**
 * Page to list and view processed drawing pages for a bid.
 */
export default function DocumentsPage() {
  const { id: bidId } = useParams();
  const navigate = useNavigate();
  const { listTree, downloadPage, updatePageMeta, batchApprovePages, deletePage, uploadBinder, getDrawingsStatus } = useDrawingsApi();
  const [tree, setTree] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewerUrl, setViewerUrl] = useState(null);
  const [viewerPageId, setViewerPageId] = useState(null);
  const [editingPage, setEditingPage] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [inlineEdit, setInlineEdit] = useState(false);
  const [showOnlyNeedsReview, setShowOnlyNeedsReview] = useState(false);
  const [inlineEdits, setInlineEdits] = useState({});
  const [previewPageId, setPreviewPageId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  // Set initial preview page when entering inline edit mode
  useEffect(() => {
    if (inlineEdit && loaded) {
      const pagesFlat = Object.values(tree)
        .flat()
        .filter(page => (showOnlyNeedsReview ? page.needsReview : true));
      if (pagesFlat.length) setPreviewPageId(pagesFlat[0].id);
    }
  }, [inlineEdit, loaded, tree, showOnlyNeedsReview]);
  useEffect(() => {
    if (inlineEdit && previewPageId) {
      (async () => {
        try {
          const url = await downloadPage(bidId, previewPageId);
          setPreviewUrl(url);
        } catch (err) { console.error(err); }
      })();
    }
  }, [inlineEdit, previewPageId, downloadPage, bidId]);
  const [saving, setSaving] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const reviewPages = useMemo(
    () => Object.values(tree).flat().filter(page => page.needsReview),
    [tree]
  );

  // Fetch drawing tree on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchTree = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listTree(bidId);
        setTree(data);
      } catch (err) {
        console.error(err);
        setError('Failed to load drawing tree');
      } finally {
        setLoading(false);
        setLoaded(true);
      }
    };
    fetchTree();
  }, [bidId, listTree]);
  // Initialize inlineEdits when tree loads
  useEffect(() => {
    if (!loaded) return;
    const edits = {};
    Object.values(tree).flat().forEach(page => {
      edits[page.id] = {
        drawingNumber: page.drawingNumber,
        drawingTitle: page.drawingTitle,
        drawingType: page.drawingType,
        drawingRevDate: page.drawingRevDate
      };
    });
    setInlineEdits(edits);
  }, [tree, loaded]);

  const handleSelect = async (pageId) => {
    try {
      const url = await downloadPage(bidId, pageId);
      setViewerUrl(url);
      setViewerPageId(pageId);
    } catch (err) {
      console.error(err);
      setError('Failed to download page');
    }
  };
  // Delete a drawing page
  const handleDelete = async (pageId) => {
    if (!window.confirm('Delete this drawing? This cannot be undone.')) return;
    try {
      await deletePage(bidId, pageId);
      toast.success('Drawing deleted');
      setViewerUrl(null);
      setViewerPageId(null);
      const refreshed = await listTree(bidId);
      setTree(refreshed);
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete drawing');
    }
  };
  // Change drawing category (scope)
  const handleCategoryChange = async (pageId, newScope) => {
    try {
      await updatePageMeta(bidId, pageId, { drawingType: newScope });
      const refreshed = await listTree(bidId);
      setTree(refreshed);
      toast.success('Category updated');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update category');
    }
  };
  // For inline editing: save a specific field for a page
  const handleInlineSave = async (pageId, field) => {
    const key = `${pageId}_${field}`;
    try {
      setSaving(prev => ({ ...prev, [key]: true }));
      const value = inlineEdits[pageId][field];
      await updatePageMeta(bidId, pageId, { [field]: value });
      // Update tree state
      setTree(prev => {
        const newTree = {};
        Object.entries(prev).forEach(([type, pages]) => {
          newTree[type] = pages.map(p =>
            p.id === pageId ? { ...p, [field]: value } : p
          );
        });
        return newTree;
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to save change');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };
  // Filter grouped data based on needsReview toggle and search term
  const filteredGrouped = useMemo(() => {
    const filtered = {};
    const term = searchTerm.trim().toLowerCase();
    Object.entries(tree).forEach(([type, pages]) => {
      let list = pages;
      if (showOnlyNeedsReview) {
        list = list.filter(p => p.needsReview);
      }
      if (term) {
        list = list.filter(p =>
          (p.drawingNumber || '').toLowerCase().includes(term) ||
          (p.drawingTitle || '').toLowerCase().includes(term) ||
          (p.title || '').toLowerCase().includes(term)
        );
      }
      if (list.length) filtered[type] = list;
    });
    return filtered;
  }, [tree, showOnlyNeedsReview, searchTerm]);

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Documents</h1>
        <button
          disabled
          className="bg-gray-300 text-gray-600 px-4 py-2 rounded cursor-not-allowed"
          title="Upload functionality coming soon"
        >
          Upload New Drawing
        </button>
      </div>
      {!inlineEdit && (
        <div className="flex items-center space-x-4 mb-4">
        {/* Binder upload control */}
        <label className="bg-blue-600 text-white px-4 py-2 rounded cursor-pointer hover:bg-blue-700">
          Upload Binder PDF
          <input
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={async e => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                await uploadBinder(bidId, file);
                toast.info('Binder upload started');
                // Poll for processing status and navigate when done
                const poll = async () => {
                  const status = await getDrawingsStatus(bidId);
                  if (status.state === 'done') {
                    navigate(`/bids/${bidId}/edit`);
                  } else {
                    setTimeout(poll, 3000);
                  }
                };
                poll();
              } catch (err) {
                console.error(err);
                toast.error('Upload failed');
              }
            }}
          />
        </label>
          <label className="flex items-center space-x-1">
            <input
              type="checkbox"
              checked={showOnlyNeedsReview}
              onChange={e => setShowOnlyNeedsReview(e.target.checked)}
            />
            <span>Only Show Pages Needing Review</span>
          </label>
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            onClick={() => setInlineEdit(true)}
          >Inline Edit Mode</button>
        </div>
      )}
      {loading && <p>Loading drawings...</p>}
      {error && <p className="text-red-500 mb-2">{error}</p>}
      {inlineEdit && (
        <div className="flex h-[calc(100vh-64px)]">
          <div className="w-1/2 border-r p-2 flex flex-col">
            {previewUrl ? (
                <Document
                file={previewUrl}
                onLoadSuccess={() => null}
                loading={<div className="text-center p-4">Loading preview...</div>}
                error={<div className="text-red-500 text-center p-4">Failed to load preview</div>}
              >
                <Page pageNumber={1} width={window.innerWidth * 0.45} />
              </Document>
            ) : (
              <p className="text-center text-gray-500">Loading preview...</p>
            )}
          </div>
          <div className="w-1/2 overflow-auto p-2">
          <table className="w-full table-auto border-collapse">
            <thead className="bg-gray-100">
              <tr>
                <th className="border p-2">Type</th>
                <th className="border p-2">Number</th>
                <th className="border p-2">Title</th>
                <th className="border p-2">Rev Date</th>
                <th className="border p-2">Needs Review</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(tree).flat()
                .filter(page => !showOnlyNeedsReview || page.needsReview)
                .map(page => (
                <tr key={page.id} className="hover:bg-gray-50">
                  <td className="border p-2">{page.drawingType}</td>
                  <td className="border p-2">
                    <input
                      type="text"
                      className="w-full p-1 border rounded"
                      value={inlineEdits[page.id]?.drawingNumber || ''}
                      onChange={e => setInlineEdits(prev => ({
                        ...prev,
                        [page.id]: { ...prev[page.id], drawingNumber: e.target.value }
                      }))}
                      onBlur={() => handleInlineSave(page.id, 'drawingNumber')}
                      onKeyDown={e => e.key === 'Enter' && handleInlineSave(page.id, 'drawingNumber')}
                    />
                    {saving[`${page.id}_drawingNumber`] && <span className="text-xs text-gray-500">Saving...</span>}
                  </td>
                  <td className="border p-2">
                    <input
                      type="text"
                      className="w-full p-1 border rounded"
                      value={inlineEdits[page.id]?.drawingTitle || ''}
                      onChange={e => setInlineEdits(prev => ({
                        ...prev,
                        [page.id]: { ...prev[page.id], drawingTitle: e.target.value }
                      }))}
                      onBlur={() => handleInlineSave(page.id, 'drawingTitle')}
                      onKeyDown={e => e.key === 'Enter' && handleInlineSave(page.id, 'drawingTitle')}
                    />
                    {saving[`${page.id}_drawingTitle`] && <span className="text-xs text-gray-500">Saving...</span>}
                  </td>
                  <td className="border p-2">
                    <input
                      type="text"
                      className="w-full p-1 border rounded"
                      value={inlineEdits[page.id]?.drawingRevDate || ''}
                      onChange={e => setInlineEdits(prev => ({
                        ...prev,
                        [page.id]: { ...prev[page.id], drawingRevDate: e.target.value }
                      }))}
                      onBlur={() => handleInlineSave(page.id, 'drawingRevDate')}
                      onKeyDown={e => e.key === 'Enter' && handleInlineSave(page.id, 'drawingRevDate')}
                    />
                    {saving[`${page.id}_drawingRevDate`] && <span className="text-xs text-gray-500">Saving...</span>}
                  </td>
                  <td className="border p-2">
                    <select
                      className="w-full p-1 border rounded"
                      value={inlineEdits[page.id]?.drawingType || ''}
                      onChange={e => {
                        setInlineEdits(prev => ({
                          ...prev,
                          [page.id]: { ...prev[page.id], drawingType: e.target.value }
                        }));
                        handleInlineSave(page.id, 'drawingType');
                      }}
                    >
                      {[
                        'Architectural','Structural Steel','Mechanical',
                        'Electrical','Civil','Landscape','Miscellaneous'
                      ].map(type => <option key={type} value={type}>{type}</option>)}
                    </select>
                    {saving[`${page.id}_drawingType`] && <span className="text-xs text-gray-500">Saving...</span>}
                  </td>
                  <td className="border p-2 text-center">{page.needsReview ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2">
            <button
              className="bg-gray-300 text-gray-700 px-3 py-1 rounded"
              onClick={() => setInlineEdit(false)}
            >Exit Inline Edit</button>
          </div>
        </div>
      </div>
      )}
      { !inlineEdit && loaded && reviewPages.length > 0 && (
        <ReviewDrawingsTable
          bidId={bidId}
          pages={reviewPages}
          onApprove={async (pageId) => {
            try {
              await updatePageMeta(bidId, pageId, { needsReview: false });
              setTree(prev => {
                const newTree = {};
                Object.entries(prev).forEach(([type, pages]) => {
                  newTree[type] = pages.map(p =>
                    p.id === pageId ? { ...p, needsReview: false } : p
                  );
                });
                return newTree;
              });
              toast.success('Approved drawing');
            } catch (err) {
              console.error(err);
              toast.error('Failed to approve drawing');
            }
          }}
          onApproveAll={async () => {
            try {
              const { updated } = await batchApprovePages(bidId);
              toast.success(`Approved ${updated} drawings.`);
              const refreshed = await listTree(bidId);
              setTree(refreshed);
            } catch (err) {
              console.error(err);
              toast.error('Failed to approve drawings');
            }
          }}
        />
      )}
      { !inlineEdit && loaded && reviewPages.length === 0 && (
        <>
          <p className="text-green-600 mb-4">All drawings reviewed and approved!</p>
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search drawings by number or title..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full p-2 border rounded"
            />
          </div>
          <DrawingTreeView
            grouped={filteredGrouped}
            onSelect={handleSelect}
            onEdit={page => setEditingPage(page)}
            onCategoryChange={handleCategoryChange}
          />
        </>
      )}

      {viewerUrl && (
        <PDFViewer
          url={viewerUrl}
          onClose={() => { setViewerUrl(null); setViewerPageId(null); }}
          onDelete={() => handleDelete(viewerPageId)}
        />
      )}
      {editingPage && (
        <EditDrawingModal
          page={editingPage}
          onClose={() => setEditingPage(null)}
          onSave={async updates => {
            try {
              await updatePageMeta(bidId, editingPage.id, updates);
              setEditingPage(null);
              const refreshed = await listTree(bidId);
              setTree(refreshed);
            } catch (err) {
              console.error(err);
              setError('Failed to update metadata');
            }
          }}
        />
      )}
    </div>
  );
}